import { List, fromJS, is } from "immutable"
import { stanceWithCustomLabelByValue } from "shared/stance/helperFunctions"
import { updateBoardNameWithStanceCustomLabel } from "shared/profiles/tracking_dashboard/helperFunctions"

/**
 * Helper fn to grab the amount of years bn this year and 2014 for the Select year dropdown
 *
 * @param {current_year} current_year
 */
export const getYearsForDropDown = (current_year) => {
    const numberOfYears = current_year - 2014 + 1
    return Array.from({ length: numberOfYears }, (_, i) => current_year - numberOfYears + i + 1).reverse()
}

/**
 * This function is used to set the initial value for issues_saved_search
 * @name createInitialIssuesSavedSearchObject
 * @function
 * @param {Number} allIssuesCount - count of all issues available to a user
 * @returns {Object} - saved search with necessary keys: added_ids, count, deleted_ids, and filters
 * This function returns an object representing the fields of a saved search
 * which will return all Issues for a user
 */
export const createInitialIssuesSavedSearchObject = (allIssuesCount) => ({
    count: allIssuesCount,
    added_ids: [],
    deleted_ids: [],
    filters: [{ archived: false }],
})

/**
 * This function is used to check if an Immutable.Map representing a saved search
 * is an all issues saved search
 * @name isAllIssuesSavedSearch
 * @function
 * @param {Immutable.Map} issueSavedSearch - stored issue_saved_search
 * @returns {Boolean} - whether issuesSavedSearch is equivalent to default all issues saved search
 */
export const isAllIssuesSavedSearch = (issueSavedSearch) =>
    Boolean(
        issueSavedSearch &&
            is(issueSavedSearch.get("filters"), fromJS([{ archived: false }])) &&
            is(issueSavedSearch.get("added_ids"), List([])) &&
            is(issueSavedSearch.get("deleted_ids"), List([])),
    )

/**
 * Build string by replacing "Bills" in data_type_specific_label for BoardType with plural value based on QDT
 * and using stance custom labels for support and oppose to replace "Supported" and "Opposed" if applicable
 * @name getDataSpecificLabel
 * @function
 * @param {Number} boardType - integer representing value of BoardType enum
 * @param {Number} qdt - integer representing value of QuorumDataType
 * @returns {String} - a new label for the BoardType
 */
export const getDataSpecificLabel = (
    boardType,
    qdt,
    stancesWithCustomLabels = DjangIO.app.userdata.types.LegislativeStanceType.items(),
) => {
    const boardTypeEnum = DjangIO.app.tracking_dashboards.types.BoardType.by_value(boardType)
    const qdtEnum = DjangIO.app.models.QuorumDataType.by_value(qdt)
    const updatedLabel = boardTypeEnum.data_type_specific_label
        .replace("Bills", qdtEnum.plural)
        .replace("bills", qdtEnum.plural.toLowerCase())
    // updated the display label of the board type if it is opposed_by_status or supported_by_status
    // we will replace the default stance word with the stance custom label
    if (boardType === DjangIO.app.tracking_dashboards.types.BoardType.opposed_by_status.value) {
        return updateBoardNameWithStanceCustomLabel({
            originalBoardName: updatedLabel,
            wordToReplace: "Opposed",
            stanceOriginalLabel: DjangIO.app.userdata.types.LegislativeStanceType.oppose.label,
            stanceCustomLabel: stanceWithCustomLabelByValue(
                stancesWithCustomLabels,
                DjangIO.app.userdata.types.LegislativeStanceType.oppose.value,
            ).label,
        })
    }
    if (boardType === DjangIO.app.tracking_dashboards.types.BoardType.supported_by_status.value) {
        return updateBoardNameWithStanceCustomLabel({
            originalBoardName: updatedLabel,
            wordToReplace: "Supported",
            stanceOriginalLabel: DjangIO.app.userdata.types.LegislativeStanceType.support.label,
            stanceCustomLabel: stanceWithCustomLabelByValue(
                stancesWithCustomLabels,
                DjangIO.app.userdata.types.LegislativeStanceType.support.value,
            ).label,
        })
    }
    return updatedLabel
}

/**
 * sorting function to sort items based on new_order field
 * @name newOrderSort
 * @function
 * @param {Object} a - enum option object
 * @param {Object} b - enum opton object
 * @returns {Number} - value < 0 if a should appear before b, value > 0 if b should appear before a
 */
export const newOrderSort = (a, b) => {
    if (a.new_order != null && b.new_order != null) {
        return a.new_order - b.new_order
    }

    if (a.new_order != null) {
        return -1
    }

    return 1
}

/**
 * Build string for Search Terms step's description based on QDT label
 * @name getSearchTermStepDescription
 * @function
 * @param {String} dataTypeLabel - string of QuorumDataType's label
 * @returns {String} - a new description for the SearchTermStep
 */
export const getSearchTermStepDescription = (dataTypeLabel) =>
    `Search terms are the foundation of your unreviewed ${dataTypeLabel} tab and search terms alert. Each search term creates a separate auto-updating list of unreviewed ${dataTypeLabel}. All new ${dataTypeLabel} introduced that match your search terms will appear on your unreviewed ${dataTypeLabel} tab, and will be included in your search terms alert.`

/**
 * This function replace all operators like `["OR", "AND", "AND NOT", "WITHIN * OF"]` with the
 * result of `wrapOperatorCallback` callback function.
 *
 * @param {String} searchTerm
 * @param {Function} wrapOperatorCallback
 * @returns {Array} an array of strings and ReactElement
 * @example
 * ```
 * splitAndReplaceOperators("(operand AND operand) OR operand");
 * // output - ["(operand", <AND />, "operand)", <OR />, "operand"]
 * ```
 */
export const splitAndReplaceOperators = (searchTerm, wrapOperatorCallback) => {
    const CONDITIONAL_OPERATORS = ["OR", "AND", "AND NOT", "WITHIN"]

    // Prioritize "AND NOT" and match "WITHIN" followed by any number and "OF"
    const operatorRegex = new RegExp(`\\b(AND NOT|${CONDITIONAL_OPERATORS.join("|")} \\d+ OF)\\b`, "g")

    // Split the string by operators, keeping the operators as separate elements
    const splitString = searchTerm.split(operatorRegex)

    // Utilize the callback to wrap operators in components
    return splitString.map((el) => (operatorRegex.test(el) ? wrapOperatorCallback(el) : el)).flat()
}
